/* eslint-disable no-nested-ternary, no-await-in-loop */
import type {
  ProcessLogEntry,
  ProcessJob,
  ProcessQueueStatus,
  ProcessQueueTrigger,
} from '@musedrops/shared';
import {
  fromDbKeys,
  toDbKeys,
  withUpdateTimestamp,
  logger,
  getProcessRoute as sharedGetProcessRoute,
  getCallbackFunction as sharedGetCallbackFunction,
} from '@musedrops/shared';
import { supabase } from '../services/supabase';

export async function getProcessQueue(
  filters: Partial<Pick<ProcessJob, 'status' | 'entityType' | 'trigger'>> = {}
): Promise<ProcessJob[]> {
  let query = supabase.from('process_queue').select('*');

  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.entityType) {
    query = query.eq('entity_type', filters.entityType);
  }
  if (filters.trigger) {
    query = query.eq('trigger', filters.trigger);
  }

  const { data, error } = await query.order('updated_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch process queue: ${error.message}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<ProcessJob>(row as Record<string, unknown>)
  );
}

export async function clearFailedQueueJobs(): Promise<void> {
  const { error } = await supabase
    .from('process_queue')
    .delete()
    .eq('status', 'failed');

  if (error) {
    throw new Error(`Failed to clear failed jobs: ${error.message}`);
  }
}

export async function cancelPendingQueueJobs(): Promise<void> {
  const { error } = await supabase
    .from('process_queue')
    .update(toDbKeys({ status: 'cancelled' as ProcessQueueStatus }))
    .in('status', ['pending']);

  if (error) {
    throw new Error(`Failed to cancel pending jobs: ${error.message}`);
  }
}

export async function clearAllQueueJobs(): Promise<void> {
  const { error } = await supabase.from('process_queue').delete().neq('id', 0); // Delete all rows

  if (error) {
    throw new Error(`Failed to clear queue: ${error.message}`);
  }
}

export async function listProcessLogs(): Promise<ProcessLogEntry[]> {
  const { data, error } = await supabase
    .from('process_logs')
    .select('*')
    .order('id', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch process logs: ${error.message}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<ProcessLogEntry>(row as Record<string, unknown>)
  );
}

export async function clearProcessLogs(): Promise<void> {
  const { error } = await supabase.from('process_logs').delete().neq('id', 0);

  if (error) {
    throw new Error(`Failed to clear process logs: ${error.message}`);
  }
}

// ============================================================================
// Queue Management Functions
// ============================================================================

/**
 * Add a new job to the process queue
 *
 * @param params.entityType - Type of entity (e.g., 'story', 'show')
 * @param params.entityId - ID of the entity
 * @param params.process - Process type (e.g., 'gen-story-voice', 'gen-story-script')
 * @param params.trigger - Job trigger type: 'manual' for UI, 'lifecycle' for automated (default: 'manual')
 * @param params.scheduledFor - When to run the job (default: now for manual)
 * @param params.maxRetries - Max retry attempts (default: 3)
 * @param params.metadata - Additional metadata (legacy, prefer payload)
 * @param params.dependsOn - Job ID this job depends on
 * @param params.callbackFunction - Edge Function to call on completion (e.g., 'gen-story-voice-callback')
 * @param params.payload - All parameters for Cloud Run execution
 */
export async function enqueueJob(params: {
  entityType: string;
  entityId: string;
  process: string;
  trigger?: ProcessQueueTrigger;
  scheduledFor?: Date | null;
  maxRetries?: number;
  metadata?: Record<string, unknown>;
  dependsOn?: number;
  // Callback pattern fields
  callbackFunction?: string | null;
  payload?: Record<string, unknown>;
}): Promise<ProcessJob> {
  const trigger = params.trigger || 'manual';

  // For manual jobs without explicit scheduledFor, default to now
  const scheduledFor = params.scheduledFor
    ? params.scheduledFor
    : trigger === 'manual'
      ? new Date()
      : null;

  const queueItem = toDbKeys({
    entityType: params.entityType,
    entityId: params.entityId,
    process: params.process,
    // Only use 'scheduled' status for future jobs, manual jobs should be 'pending'
    status: scheduledFor && scheduledFor > new Date() ? 'scheduled' : 'pending',
    trigger,
    scheduledFor: scheduledFor?.toISOString() || null,
    maxRetries: params.maxRetries ?? 3,
    retryCount: 0,
    attemptCount: 0,
    metadata: params.metadata || null,
    dependsOn: params.dependsOn || null,
    // Priority derived from trigger: manual jobs are priority
    priority: trigger === 'manual',
    callbackFunction: params.callbackFunction ?? null,
    payload: params.payload ?? null,
  });

  const { data, error } = await supabase
    .from('process_queue')
    .insert(withUpdateTimestamp(queueItem))
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to enqueue job: ${error.message}`);
  }

  // Trigger queue processor for manual jobs (immediate processing)
  if (trigger === 'manual') {
    // Trigger asynchronously without awaiting to avoid blocking
    supabase.functions
      .invoke('process-queue', {
        body: { trigger: 'immediate' },
      })
      .catch((err: unknown) => {
        // Log but don't fail - queue processor failure shouldn't block enqueue
        logger.warn('Failed to trigger queue processor', { error: err });
      });
  }

  return fromDbKeys<ProcessJob>(data);
}

/**
 * Get next pending job from queue (with dependency check)
 */
export async function dequeueNextJob(
  processName?: string
): Promise<ProcessJob | null> {
  let query = supabase
    .from('process_queue')
    .select('*')
    .eq('status', 'pending')
    .is('depends_on', null) // Only jobs with no dependencies
    .order('id', { ascending: true })
    .limit(1);

  if (processName) {
    query = query.eq('process', processName);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to dequeue job: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return null;
  }

  return fromDbKeys<ProcessJob>(data[0]);
}

/**
 * Get scheduled jobs that are ready to run
 */
export async function getScheduledJobsReady(): Promise<ProcessJob[]> {
  const { data, error } = await supabase
    .from('process_queue')
    .select('*')
    .eq('status', 'scheduled')
    .lte('scheduled_for', new Date().toISOString())
    .order('scheduled_for', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch scheduled jobs: ${error.message}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<ProcessJob>(row as Record<string, unknown>)
  );
}

/**
 * Update job status
 */
export async function updateJobStatus(params: {
  id: number;
  status: ProcessQueueStatus;
  lastError?: string | null;
  incrementAttempt?: boolean;
}): Promise<void> {
  const updates: Record<string, unknown> = {
    status: params.status,
  };

  if (params.lastError !== undefined) {
    updates.last_error = params.lastError;
  }

  if (params.incrementAttempt) {
    // Need to fetch current attempt_count first
    const { data: current } = await supabase
      .from('process_queue')
      .select('attempt_count')
      .eq('id', params.id)
      .single();

    if (current) {
      updates.attempt_count = (current.attempt_count || 0) + 1;
    }
  }

  const { error } = await supabase
    .from('process_queue')
    .update(withUpdateTimestamp(updates))
    .eq('id', params.id);

  if (error) {
    throw new Error(`Failed to update job status: ${error.message}`);
  }
}

/**
 * Mark job as processing
 */
export async function markJobProcessing(id: number): Promise<void> {
  await updateJobStatus({ id, status: 'processing', incrementAttempt: true });
}

/**
 * Mark job as completed
 */
export async function markJobCompleted(id: number): Promise<void> {
  await updateJobStatus({ id, status: 'completed' });
}

/**
 * Mark job as failed and handle retry logic
 */
export async function markJobFailed(params: {
  id: number;
  error: string;
}): Promise<void> {
  const { data: job } = await supabase
    .from('process_queue')
    .select('*')
    .eq('id', params.id)
    .single();

  if (!job) {
    throw new Error(`Job ${params.id} not found`);
  }

  const queueItem = fromDbKeys<ProcessJob>(job);
  const shouldRetry = queueItem.attemptCount < queueItem.maxRetries;

  if (shouldRetry) {
    // Requeue for retry
    await updateJobStatus({
      id: params.id,
      status: 'pending',
      lastError: params.error,
    });

    // Increment retry count
    const { error: updateError } = await supabase
      .from('process_queue')
      .update({ retry_count: queueItem.retryCount + 1 })
      .eq('id', params.id);

    if (updateError) {
      throw new Error(`Failed to update retry count: ${updateError.message}`);
    }
  } else {
    // Max retries reached
    await updateJobStatus({
      id: params.id,
      status: 'failed',
      lastError: params.error,
    });
  }
}

/**
 * Remove completed/failed jobs older than specified days
 */
export async function cleanupOldJobs(daysOld = 7): Promise<number> {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysOld);

  const { error, count } = await supabase
    .from('process_queue')
    .delete({ count: 'exact' })
    .in('status', ['completed', 'failed', 'cancelled'])
    .lt('updated_at', cutoffDate.toISOString());

  if (error) {
    throw new Error(`Failed to cleanup old jobs: ${error.message}`);
  }

  return count || 0;
}

/**
 * Get stuck jobs (processing for too long)
 */
export async function getStuckJobs(minutesStuck = 30): Promise<ProcessJob[]> {
  const cutoffTime = new Date();
  cutoffTime.setMinutes(cutoffTime.getMinutes() - minutesStuck);

  const { data, error } = await supabase
    .from('process_queue')
    .select('*')
    .eq('status', 'processing')
    .lt('updated_at', cutoffTime.toISOString());

  if (error) {
    throw new Error(`Failed to fetch stuck jobs: ${error.message}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<ProcessJob>(row as Record<string, unknown>)
  );
}

/**
 * Reset stuck jobs back to pending
 */
export async function resetStuckJobs(minutesStuck = 30): Promise<number> {
  const stuckJobs = await getStuckJobs(minutesStuck);

  for (const job of stuckJobs) {
    await updateJobStatus({
      id: job.id,
      status: 'pending',
      lastError: `Job stuck for ${minutesStuck} minutes, reset to pending`,
    });
  }

  return stuckJobs.length;
}

/**
 * Log a process event
 */
export async function logProcessEvent(
  entry: Omit<ProcessLogEntry, 'id'>
): Promise<void> {
  const { error } = await supabase.from('process_logs').insert(toDbKeys(entry));

  if (error) {
    throw new Error(`Failed to log process event: ${error.message}`);
  }
}

/**
 * Delete a completed queue job after it has been logged.
 * This is called after successful logging to keep the queue clean.
 * If deletion fails, logs the error but does not throw (job remains in queue for manual cleanup).
 */
export async function deleteCompletedQueueJob(jobId: number): Promise<void> {
  const { error } = await supabase
    .from('process_queue')
    .delete()
    .eq('id', jobId);

  if (error) {
    // Log error but don't throw - user will manually clean stuck jobs if needed
    logger.warn('Failed to delete completed queue job', {
      jobId,
      error: error.message,
    });
  }
}

// ============================================================================
// Callback Pattern Functions
// ============================================================================

/**
 * Update job with completion output (called by queue-job-complete)
 */
export async function updateJobOutput(params: {
  id: number;
  success: boolean;
  output?: Record<string, unknown>;
  error?: string;
}): Promise<ProcessJob | null> {
  const updates: Record<string, unknown> = {
    status: params.success ? 'completed' : 'failed',
  };

  if (params.output) {
    updates.output = params.output;
  }

  if (params.error) {
    updates.last_error = params.error;
  }

  const { data, error } = await supabase
    .from('process_queue')
    .update(withUpdateTimestamp(updates))
    .eq('id', params.id)
    .select()
    .single();

  if (error) {
    logger.error('Failed to update job output', {
      jobId: params.id,
      error: error.message,
    });
    return null;
  }

  return fromDbKeys<ProcessJob>(data);
}

/**
 * Get a job by ID (for callback lookup)
 */
export async function getJobById(jobId: number): Promise<ProcessJob | null> {
  const { data, error } = await supabase
    .from('process_queue')
    .select('*')
    .eq('id', jobId)
    .single();

  if (error) {
    logger.error('Failed to get job by ID', { jobId, error: error.message });
    return null;
  }

  return fromDbKeys<ProcessJob>(data);
}

/**
 * Build Cloud Run URL for a given process type
 *
 * Issue #617: Now uses canonical PROCESS_ROUTES from shared
 */
export function getCloudRunUrl(processType: string): string | null {
  const cloudRunBaseUrl =
    process.env.CLOUD_RUN_GEN_SERVICE_URL ||
    process.env.VITE_CLOUD_RUN_GEN_SERVICE_URL;
  if (!cloudRunBaseUrl) {
    logger.error('CLOUD_RUN_GEN_SERVICE_URL environment variable not set');
    return null;
  }

  // Issue #617: Use canonical process routing from shared
  const route = sharedGetProcessRoute(processType);
  if (!route) {
    logger.error('Unknown process type - not in PROCESS_ROUTES', { processType });
    return null;
  }

  return `${cloudRunBaseUrl}${route}`;
}

/**
 * Get callback function name for a process type
 *
 * Issue #617: Now uses canonical PROCESS_ROUTES from shared
 */
export function getCallbackFunctionName(processType: string): string | null {
  // Issue #617: Use canonical process routing from shared
  return sharedGetCallbackFunction(processType);
}
